/**
 * Charger deep-link stash for the signed-out landing.
 *
 * A shared charger link (or field QR) opened while signed out renders
 * LoginPage in place — and before this module, every auth completion
 * navigated to '/map', silently dropping the charger the person came
 * for. That made shared links dishonest for exactly the new-guest case
 * they exist for.
 *
 * Mechanism mirrors the /home-invite token stash: sessionStorage, so
 * the link survives the Google OAuth redirect round-trip in the same
 * tab and dies with the tab. Only the three charger deep-link shapes
 * (the same set apps/web/src/lib/qr.ts parses) are ever stashed, and
 * the consumed value is re-validated as a same-origin path — never an
 * absolute URL — so this cannot become an open-redirect vector.
 *
 * TTL keeps a stale stash from teleporting someone who abandoned the
 * link and signs in for an unrelated reason much later.
 *
 * localStorage too (Warren, 29 Oct 2026, the v3 zero regression): a driver
 * who scans a charger, asks for a magic link and opens it from the email
 * lands in a NEW tab, where sessionStorage is empty, so the charger they
 * scanned was lost and they landed on the map. The same entry, with the
 * same TTL, is now kept in both; the tab's own copy wins when both exist,
 * and consuming clears both, so a replayed link never haunts a later
 * sign-in in another tab.
 */

import { HOST_PROGRAM_PATH, isHostProgramPath } from '@evi/shared';

const KEY = 'evi.pending_charger_link';
const TTL_MS = 30 * 60_000;

/** /charging-station/:loc, /stations/:loc, /stations/:loc/stall/:charger —
 *  location ids and OCPP identities use the same alphabets App.tsx routes on,
 *  upper-case hex and a trailing slash included (lib/stickerUrl, P0 2026-10-23). */
const CHARGER_PATH_RE =
  /^\/(charging-station\/[A-Za-z0-9][A-Za-z0-9-]*|stations\/[A-Za-z0-9][A-Za-z0-9-]*(\/stall\/[A-Za-z0-9][A-Za-z0-9_-]*)?)\/?$/;

/** Stash the current URL when (and only when) it is a charger deep link.
 *  Idempotent — re-renders of the unauthenticated branch just rewrite
 *  the same entry. No-op on non-charger paths and in storage-less
 *  contexts (private mode quota errors are swallowed). */
export function stashChargerDeepLink(): void {
  let entry: string;
  try {
    const { pathname, search } = window.location;
    const hostCampaign = isHostProgramPath(pathname);
    if (!hostCampaign && !CHARGER_PATH_RE.test(pathname)) return;
    entry = JSON.stringify({ path: hostCampaign ? HOST_PROGRAM_PATH : pathname + search, at: Date.now() });
  } catch {
    return;
  }
  // Each storage on its own: one that is blocked must not stop the other.
  try { sessionStorage.setItem(KEY, entry); } catch { /* not replayed from this tab */ }
  try { localStorage.setItem(KEY, entry); } catch { /* not replayed in a new tab */ }
}

/** Read and clear the entry in one storage; null when absent or unreadable. */
function takeFrom(storage: () => Storage): string | null {
  try {
    const s = storage();
    const raw = s.getItem(KEY);
    s.removeItem(KEY);
    return raw;
  } catch {
    return null;
  }
}

/** Pop the stashed link. Returns a same-origin path to navigate to, or
 *  null when nothing usable is stashed. Always clears the entry — a
 *  failed replay must not haunt the next sign-in. */
export function consumePendingDeepLink(): string | null {
  // Both are always cleared; this tab's copy wins over the shared one.
  const fromTab = takeFrom(() => sessionStorage);
  const shared = takeFrom(() => localStorage);
  const raw = fromTab ?? shared;
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { path?: unknown; at?: unknown };
    if (typeof parsed.path !== 'string' || !parsed.path.startsWith('/') || parsed.path.startsWith('//')) {
      return null;
    }
    if (typeof parsed.at !== 'number' || Date.now() - parsed.at > TTL_MS) return null;
    return isHostProgramPath(parsed.path) ? HOST_PROGRAM_PATH : parsed.path;
  } catch {
    return null;
  }
}
